import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import { CloudUpload, Download, Visibility } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const MediaManagement = () => {
    const [loading, setLoading] = useState(false);
    const [phoneNumberId, setPhoneNumberId] = useState("");
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaType, setMediaType] = useState("image");
    const [uploadedMedia, setUploadedMedia] = useState(null);
    const [mediaId, setMediaId] = useState("");
    const [mediaUrl, setMediaUrl] = useState(null);
    const showNotification = useNotification();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMediaFile(file);
        }
    };

    const handleUpload = async () => {
        if (!mediaFile) {
            showNotification("Please select a file", "error");
            return;
        }

        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || null;
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const formData = new FormData();
            formData.append("file", mediaFile);
            formData.append("type", mediaType);

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/media`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setUploadedMedia(response.data);
            showNotification("Media uploaded successfully!", "success");
        } catch (error) {
            console.error("Error uploading media:", error);
            showNotification(
                error.response?.data?.error || "Failed to upload media",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleGetMediaUrl = async () => {
        if (!mediaId.trim()) {
            showNotification("Please enter Media ID", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/media/${mediaId}`
            );

            setMediaUrl(response.data);
            showNotification("Media URL fetched successfully!", "success");
        } catch (error) {
            console.error("Error fetching media URL:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch media URL",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadMedia = async () => {
        if (!mediaId.trim()) {
            showNotification("Please enter Media ID", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/media/${mediaId}/download`,
                {
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `media_${mediaId}`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            showNotification("Media downloaded successfully!", "success");
        } catch (error) {
            console.error("Error downloading media:", error);
            showNotification(
                error.response?.data?.error || "Failed to download media",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Media Management
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Upload Section */}
            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Upload Media
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone Number ID (Optional - override saved value)"
                                variant="outlined"
                                size="small"
                                value={phoneNumberId}
                                onChange={(e) => setPhoneNumberId(e.target.value)}
                                placeholder="Enter Phone Number ID to override saved value"
                                helperText="Leave empty to use saved value from Settings"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Media Type</InputLabel>
                                <Select
                                    value={mediaType}
                                    onChange={(e) => setMediaType(e.target.value)}
                                >
                                    <MenuItem value="image">Image</MenuItem>
                                    <MenuItem value="video">Video</MenuItem>
                                    <MenuItem value="document">Document</MenuItem>
                                    <MenuItem value="audio">Audio</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUpload />}
                            >
                                {mediaFile ? mediaFile.name : "Select File"}
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                />
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                startIcon={<CloudUpload />}
                                disabled={!phoneNumberId || !mediaFile}
                                sx={{ ...WB_SX.primaryButton }}
                            >
                                Upload Media
                            </Button>
                        </Grid>
                    </Grid>

                    {uploadedMedia && (
                        <Box sx={WB_SX.infoBox}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                Upload Successful!
                            </Typography>
                            <Typography variant="body2">
                                <strong>Media ID:</strong> {uploadedMedia.id}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Get Media URL Section */}
            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Get Media URL
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Media ID"
                                variant="outlined"
                                size="small"
                                value={mediaId}
                                onChange={(e) => setMediaId(e.target.value)}
                                placeholder="Enter Media ID"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="contained"
                                onClick={handleGetMediaUrl}
                                startIcon={<Visibility />}
                                fullWidth
                                disabled={!mediaId}
                                sx={{ ...WB_SX.primaryButton }}
                            >
                                Get URL
                            </Button>
                        </Grid>
                    </Grid>

                    {mediaUrl && (
                        <Box sx={WB_SX.infoBox}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                Media URL:
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                {mediaUrl.url}
                            </Typography>
                            {mediaUrl.mime_type && (
                                <Typography variant="body2">
                                    <strong>MIME Type:</strong> {mediaUrl.mime_type}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Download Media Section */}
            <Card sx={WB_SX.card}>
                <CardContent sx={WB_SX.cardContent}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Download Media
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Media ID"
                                variant="outlined"
                                size="small"
                                value={mediaId}
                                onChange={(e) => setMediaId(e.target.value)}
                                placeholder="Enter Media ID"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleDownloadMedia}
                                startIcon={<Download />}
                                fullWidth
                                disabled={!mediaId}
                            >
                                Download
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default MediaManagement;

